import { useCallback } from "react";
import {
  EventSourceMessage,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { nuclearn_api_url } from "src/App";
import { authorizedUser, redirectToLogin } from "src/helpers/Auth";
import { useVnvAssistStore } from "../store/VnvAssistStore";
import type { ClaimState } from "../store/VnvAssistStore";
import { getVnvAssistClaims } from "../services/VnvAssistServices";

/**
 * Phase A identification hook.
 *
 * ``POST /run/{runId}/identify`` is start-or-subscribe on the backend:
 * PENDING → atomically claim + enqueue Celery task + stream pub/sub events.
 * IDENTIFYING → stream pub/sub events. Terminal → 409, fall back to GET /claims.
 *
 * Uses ``fetchEventSource`` (same lib the main chat stream uses) so the
 * Authorization header from localStorage is attached and SSE framing is
 * handled by the lib instead of hand-rolled.
 */
export const useVnvAssistIdentification = () => {
  const {
    setValidationStatus,
    setValidationError,
    setClaims,
    addClaims,
    setIdentificationTotalChunks,
    setIdentificationChunksCompleted,
  } = useVnvAssistStore();

  const handleIdentificationEvent = useCallback(
    (payload: Record<string, unknown>) => {
      const eventType = payload.event_type;

      if (eventType === "identification_started") {
        const totalChunks = payload.total_chunks;
        setIdentificationTotalChunks(
          typeof totalChunks === "number" ? totalChunks : null,
        );
      } else if (eventType === "chunk_progress") {
        const idx = payload.chunk_index;
        if (typeof idx === "number") setIdentificationChunksCompleted(idx);
      } else if (eventType === "claims_identified") {
        const incoming = (payload.claims as Record<string, unknown>[]) ?? [];
        const mapped: ClaimState[] = incoming.map((c) => ({
          id: c.id as number,
          text: c.text as string,
          page_number: (c.page_number as number | null) ?? null,
          source_citation: (c.source_citation as string | null) ?? null,
          source_chunk_fk: null,
          score: null,
          rationale: null,
          status: (c.status as ClaimState["status"]) ?? "identified",
        }));
        if (mapped.length > 0) {
          addClaims(mapped);
        }
      } else if (eventType === "identification_complete") {
        setValidationStatus("identified");
      } else if (eventType === "error") {
        setValidationError(
          (payload.message as string | null) ?? "Unknown error",
        );
        setValidationStatus("error");
      }
    },
    [
      addClaims,
      setValidationStatus,
      setValidationError,
      setIdentificationTotalChunks,
      setIdentificationChunksCompleted,
    ],
  );

  const startIdentification = useCallback(
    async (signal?: AbortSignal) => {
      const { vnvRunId: currentRunId, validationStatus: currentStatus } =
        useVnvAssistStore.getState();
      if (!currentRunId) return;
      if (currentStatus === "identifying") return;

      setValidationStatus("identifying");
      setValidationError(null);

      const user = authorizedUser();
      if (!user.Authorization) {
        redirectToLogin();
        return;
      }

      try {
        await fetchEventSource(
          `${nuclearn_api_url}/products/vnv-assist/run/${currentRunId}/identify`,
          {
            method: "POST",
            openWhenHidden: true,
            signal,
            headers: {
              Authorization: user.Authorization,
              "Content-Type": "application/json",
            },
            async onopen(response) {
              if (response.ok) return;

              if (response.status === 401 || response.status === 403) {
                redirectToLogin();
                throw new Error("Unauthorized");
              }

              if (response.status === 409) {
                // Run is terminal — hydrate from GET /claims and settle.
                try {
                  const claims = await getVnvAssistClaims(currentRunId);
                  if (!signal?.aborted) {
                    setClaims(claims);
                    setValidationStatus("identified");
                  }
                } catch {
                  if (!signal?.aborted) {
                    setValidationStatus("error");
                    setValidationError(
                      "Run is complete but claims could not be loaded.",
                    );
                  }
                }
                // Throwing aborts the stream without reconnecting.
                throw new Error("terminal");
              }

              throw new Error(
                `Identification request failed: ${response.status}`,
              );
            },
            onmessage(event: EventSourceMessage) {
              if (signal?.aborted) return;
              if (!event.data) return;
              try {
                const payload = JSON.parse(event.data);
                handleIdentificationEvent(payload);
              } catch {
                // ignore parse errors
              }
            },
            onclose() {
              if (signal?.aborted) return;
              const status = useVnvAssistStore.getState().validationStatus;
              if (status === "identifying") {
                setValidationStatus("identified");
              }
            },
            onerror(err) {
              // Don't retry — abort the stream by rethrowing.
              throw err;
            },
          },
        );
      } catch (err) {
        if (signal?.aborted) return;
        // 409-fallback and 401/403 already set state above; skip overwriting.
        if (err instanceof Error && err.message === "terminal") return;
        if (err instanceof Error && err.message === "Unauthorized") return;
        const msg =
          err instanceof Error ? err.message : "Identification failed";
        setValidationError(msg);
        setValidationStatus("error");
      }
    },
    [
      setValidationStatus,
      setValidationError,
      setClaims,
      handleIdentificationEvent,
    ],
  );

  return { startIdentification };
};
